import { BucketPicker } from "@/components/bucket-picker";
import { CopyId } from "@/components/copy-id";
import { SiteHeader } from "@/components/site-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "@/components/ui/chart";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatDateTime } from "@/lib/formatters";
import { queryKeys } from "@/lib/queryKeys";
import { cn } from "@/lib/utils";
import { ApiError } from "@/providers/base.provider";
import {
  countersProvider,
  type CounterDefinition,
  type CounterPeriod,
  type CounterRowSort,
  type CounterSeries,
  type CounterTotal,
  type CounterUniverse,
  type CounterUserSort,
} from "@/providers/counters.provider";
import { usersProvider } from "@/providers/users.provider";
import { IconChevronLeft, IconChevronRight, IconSearch } from "@tabler/icons-react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import { useState } from "react";
import { Bar, CartesianGrid, ComposedChart, Line, XAxis, YAxis } from "recharts";

type Tab = "rows" | "totals" | "users";
type Scope = "global" | "all" | "user";
type FilterMode = "rows" | "totals";

interface Filters {
  name: string;
  scope: Scope;
  userId: string;
  period: CounterPeriod | "";
  from: string;
  to: string;
}

type RangePreset = "today" | "last-7-days" | "this-month" | "last-3-months" | "custom";

interface RankingFilters {
  name: string;
  period: CounterPeriod;
  preset: RangePreset;
  // Only used by the custom preset.
  from: string;
  to: string;
  sort: CounterUserSort;
}

// Radix Select can't hold an empty value, so "no filter" needs a sentinel.
const ANY = "any";
const ALL_PERIODS: CounterPeriod[] = ["all", "month", "day"];
const DIRECTIONS_QUOTA = 10_000;

const UNIVERSES: CounterUniverse[] = ["dev", "staging", "prod"];
const UNIVERSE_STORAGE_KEY = "counters-universe";
// The UI can't ask the backend which universe it runs as, so prod it is.
const DEFAULT_UNIVERSE: CounterUniverse = "prod";

function loadUniverse(): CounterUniverse {
  try {
    const stored = localStorage.getItem(UNIVERSE_STORAGE_KEY);
    return UNIVERSES.includes(stored as CounterUniverse) ? (stored as CounterUniverse) : DEFAULT_UNIVERSE;
  } catch {
    return DEFAULT_UNIVERSE;
  }
}

function saveUniverse(universe: CounterUniverse) {
  try {
    localStorage.setItem(UNIVERSE_STORAGE_KEY, universe);
  } catch {
    // Private mode or blocked storage: the choice just won't be remembered.
  }
}

// The API has no descriptions or units, so known counters get a label here.
const COUNTER_LABELS: Record<string, string> = {
  places_search_request: "Places searches",
  places_google_request: "Places requests sent to Google",
  map_routes_request: "Routes served (Google or Mapbox)",
  directions_google_request: "Google Directions quota",
  distance_traveled_by_user_meters: "Distance traveled",
};

const PERIOD_LABELS: Record<CounterPeriod, string> = {
  all: "All time",
  month: "Monthly",
  day: "Daily",
};

const SCOPE_LABELS: Record<Scope, string> = {
  global: "Global only",
  all: "Global + users",
  user: "One user",
};

const ROW_SORTS: { value: CounterRowSort | typeof ANY; label: string }[] = [
  { value: ANY, label: "Default" },
  { value: "value_desc", label: "Highest value" },
  { value: "value_asc", label: "Lowest value" },
  { value: "updated_desc", label: "Recently updated" },
];

const TOP_PRESETS = [
  { label: "Top travelers", name: "distance_traveled_by_user_meters" },
  { label: "Heaviest Places users", name: "places_google_request" },
] as const;

const EMPTY_FILTERS: Filters = {
  name: "",
  scope: "all",
  userId: "",
  period: "",
  from: "",
  to: "",
};

const EMPTY_RANKING_FILTERS: RankingFilters = {
  name: "",
  period: "all",
  preset: "last-7-days",
  from: "",
  to: "",
  sort: "value_desc",
};

const RANGE_PRESETS: Record<"day" | "month", { value: RangePreset; label: string }[]> = {
  day: [
    { value: "today", label: "Today" },
    { value: "last-7-days", label: "Last 7 days" },
    { value: "this-month", label: "This month" },
    { value: "custom", label: "Custom" },
  ],
  month: [
    { value: "this-month", label: "This month" },
    { value: "last-3-months", label: "Last 3 months" },
    { value: "custom", label: "Custom months" },
  ],
};

// Summing a long daily range per user is expensive on the backend.
const MAX_CUSTOM_DAYS = 366;

// Counters not listed here count requests.
const COUNTER_UNITS: Record<string, { unit: string; scale: number; decimals: number; rawUnit: string }> = {
  distance_traveled_by_user_meters: { unit: "km", scale: 1000, decimals: 1, rawUnit: "m" },
};

// Requests read fine as bare numbers in a table, so their unit is opt-in;
// scaled units always show, since the number alone would mislead.
function formatCounterValue(name: string, value: number, withRequestsUnit = false): string {
  const unit = COUNTER_UNITS[name];
  if (!unit) return withRequestsUnit ? `${value.toLocaleString()} requests` : value.toLocaleString();
  const scaled = (value / unit.scale).toLocaleString(undefined, {
    minimumFractionDigits: unit.decimals,
    maximumFractionDigits: unit.decimals,
  });
  return `${scaled} ${unit.unit}`;
}

function rawValueTitle(name: string, value: number): string | undefined {
  const unit = COUNTER_UNITS[name];
  return unit ? `${value.toLocaleString()} ${unit.rawUnit}` : undefined;
}

function formatAxisValue(name: string, value: number): string {
  const scale = COUNTER_UNITS[name]?.scale ?? 1;
  return new Intl.NumberFormat(undefined, { notation: "compact" }).format(value / scale);
}

function counterLabel(name: string): string {
  return COUNTER_LABELS[name] ?? name;
}

// ── Series rules ─────────────────────────────────────────────────────────────
// Every filter option comes from the counter's series, the exact
// (scope, period) pairs it writes, so impossible pairs can't be picked.

function seriesFor(def: CounterDefinition | undefined, scope: Scope): CounterSeries[] {
  if (!def) return [];
  if (scope === "all") return def.series;
  return def.series.filter((s) => s.scope === (scope === "global" ? "global" : "user"));
}

function scopePeriods(def: CounterDefinition | undefined, scope: Scope): CounterPeriod[] {
  if (!def) return ALL_PERIODS;
  const present = new Set(seriesFor(def, scope).map((s) => s.period));
  return ALL_PERIODS.filter((p) => present.has(p));
}

// Periods the counter writes per user. Only these can be ranked by
// /counters/by-user.
function userPeriods(def?: CounterDefinition): CounterPeriod[] {
  return def ? scopePeriods(def, "user") : [];
}

// "Global only" needs a counter with a global series: global=true across
// every counter silently drops per-user-only ones like distance.
function scopesFor(def: CounterDefinition | undefined, mode: FilterMode): Scope[] {
  if (!def) return ["all", "user"];
  const hasGlobal = def.series.some((s) => s.scope === "global");
  const hasUser = def.series.some((s) => s.scope === "user");
  // Totals would add a global and a per-user series of the same period together.
  const overlaps =
    mode === "totals" &&
    def.series.some(
      (g) => g.scope === "global" && def.series.some((u) => u.scope === "user" && u.period === g.period),
    );
  return [
    ...(hasGlobal ? (["global"] as const) : []),
    ...(overlaps ? [] : (["all"] as const)),
    ...(hasUser ? (["user"] as const) : []),
  ];
}

function defaultPeriod(def: CounterDefinition | undefined, scope: Scope, mode: FilterMode): CounterPeriod {
  const periods = scopePeriods(def, scope);
  if (mode === "totals") {
    return periods.find((p) => p === "month") ?? periods.find((p) => p === "day") ?? periods[0] ?? "month";
  }
  // The first series' period keeps the table to one comparable kind of value.
  return seriesFor(def, scope)[0]?.period ?? periods[0] ?? "all";
}

// Applied in order: unavailable scope → Global + users; period not written for
// the scope → cleared on Rows, first available on Totals; period change →
// buckets cleared, since their format follows the period. "Any period" only
// exists on Rows with All counters.
function reconcile(next: Filters, prev: Filters, def: CounterDefinition | undefined, mode: FilterMode): Filters {
  const scopes = scopesFor(def, mode);
  const scope = scopes.includes(next.scope) ? next.scope : scopes.includes("all") ? "all" : scopes[0]!;
  let period: CounterPeriod | "" =
    next.period && scopePeriods(def, scope).includes(next.period) ? next.period : "";
  if (!period && (def || mode === "totals")) period = defaultPeriod(def, scope, mode);
  const keepBuckets = (period === "month" || period === "day") && period === prev.period;
  return {
    ...next,
    scope,
    period,
    from: keepBuckets ? next.from : "",
    to: keepBuckets ? next.to : "",
  };
}

// The date pickers stop at the edges of the data the series actually holds.
function bucketBounds(def: CounterDefinition | undefined, scope: Scope, period: CounterPeriod | "") {
  const series = seriesFor(def, scope).filter((s) => s.period === period && s.first_bucket);
  if (!series.length) return {};
  return {
    min: series.map((s) => s.first_bucket).sort()[0],
    max: series.map((s) => s.last_bucket).sort().reverse()[0],
  };
}

function defaultTotalsName(defs: CounterDefinition[]): string {
  return defs.find((d) => d.name === "directions_google_request")?.name ?? defs[0]?.name ?? "";
}

function defaultPreset(period: CounterPeriod): RangePreset {
  return period === "month" ? "this-month" : "last-7-days";
}

// Buckets are UTC, so range presets are computed in UTC too.
function utcDay(offsetDays = 0): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() + offsetDays);
  return d.toISOString().slice(0, 10);
}

function utcMonth(offsetMonths = 0): string {
  const d = new Date();
  d.setUTCDate(1);
  d.setUTCMonth(d.getUTCMonth() + offsetMonths);
  return d.toISOString().slice(0, 7);
}

function resolveRange(f: RankingFilters): { from?: string; to?: string; error?: string } {
  if (f.period === "all") return {};
  if (f.period === "month") {
    if (f.preset === "last-3-months") return { from: utcMonth(-2), to: utcMonth() };
    if (f.preset === "custom") return { from: f.from || undefined, to: f.to || undefined };
    return { from: utcMonth(), to: utcMonth() };
  }
  if (f.preset === "today") return { from: utcDay(), to: utcDay() };
  if (f.preset === "this-month") return { from: `${utcMonth()}-01`, to: utcDay() };
  if (f.preset === "custom") {
    if (!f.from) return { error: "Pick a start date." };
    const days = (Date.parse(f.to || utcDay()) - Date.parse(f.from)) / 86_400_000 + 1;
    if (days > MAX_CUSTOM_DAYS) return { error: "Custom ranges are capped at one year of days." };
    return { from: f.from, to: f.to || undefined };
  }
  return { from: utcDay(-6), to: utcDay() };
}

function formatBucket(bucket: string, period: CounterPeriod): string {
  if (period === "all" || !bucket) return "All time";
  const date = new Date(`${period === "month" ? `${bucket}-01` : bucket}T00:00:00Z`);
  return date.toLocaleDateString(
    "en-US",
    period === "month"
      ? { month: "short", year: "numeric", timeZone: "UTC" }
      : { month: "short", day: "numeric", timeZone: "UTC" },
  );
}

// Errors arrive with an empty body, so the status is all there is to go on.
function countersErrorMessage(error: unknown): string {
  if (error instanceof ApiError && error.status === 400) {
    return "The server rejected these filters. Check the period, bucket range and sort.";
  }
  if (error instanceof ApiError && error.status === 502) {
    return "Couldn't load counters. Try again.";
  }
  return "Couldn't load counters. Check your connection and try again.";
}

export function CountersPage() {
  const [tab, setTab] = useState<Tab>("rows");
  const [universe, setUniverse] = useState<CounterUniverse>(loadUniverse);
  const [rowFilters, setRowFilters] = useState<Filters>(EMPTY_FILTERS);
  const [rowSort, setRowSort] = useState<CounterRowSort | undefined>(undefined);
  const [page, setPage] = useState(1);
  const [totalsFilters, setTotalsFilters] = useState<Filters>(EMPTY_FILTERS);
  const [rankingFilters, setRankingFilters] = useState<RankingFilters>(EMPTY_RANKING_FILTERS);
  const [rankingPage, setRankingPage] = useState(1);

  const { data: definitions, isLoading: definitionsLoading } = useQuery({
    queryKey: queryKeys.counterDefinitions(universe),
    queryFn: () => countersProvider.getDefinitions(universe),
    // The backend already caches for a minute; caching here too would double
    // how long a new counter takes to appear.
    staleTime: 0,
  });
  // Unscoped, only to mark which universes each counter has rows in.
  const { data: allDefinitions } = useQuery({
    queryKey: queryKeys.counterDefinitions(),
    queryFn: () => countersProvider.getDefinitions(),
    staleTime: Infinity,
  });
  const defs = definitions ?? [];
  const defFor = (name: string) => defs.find((d) => d.name === name);
  const universesOf = (name: string) => allDefinitions?.find((d) => d.name === name)?.universes ?? [];

  // A filter kept across a universe switch can name a counter this universe
  // doesn't have; say where its rows are instead of a bare "no data".
  function universeHint(name: string): string {
    if (!name || defFor(name)) return "";
    const elsewhere = universesOf(name);
    return elsewhere.length ? `This counter only has rows in ${elsewhere.join(", ")}.` : "";
  }

  function updateRowFilters(next: Filters) {
    setRowFilters(reconcile(next, rowFilters, defFor(next.name), "rows"));
    setPage(1);
  }

  function updateRowSort(sort: CounterRowSort | undefined) {
    setRowSort(sort);
    setPage(1);
  }

  function applyTopPreset(name: string) {
    setRowFilters(
      reconcile({ ...EMPTY_FILTERS, name, scope: "all", period: "all" }, rowFilters, defFor(name), "rows"),
    );
    updateRowSort("value_desc");
  }

  // Totals always need a counter and a period; reconcile fills the period in.
  const totalsName = totalsFilters.name || defaultTotalsName(defs);
  const totalsDef = defFor(totalsName);
  const totalsEffective = reconcile({ ...totalsFilters, name: totalsName }, totalsFilters, totalsDef, "totals");

  function updateTotalsFilters(next: Filters) {
    setTotalsFilters(reconcile(next, totalsEffective, defFor(next.name), "totals"));
  }

  // The ranking only works for counters with a per-user series; fall back to
  // the first one, and to the first period that series is written in.
  const rankingName = rankingFilters.name || defs.find((d) => userPeriods(d).length > 0)?.name || "";
  const rankingDef = defFor(rankingName);
  const rankingPeriods = userPeriods(rankingDef);
  const rankingEffective: RankingFilters = {
    ...rankingFilters,
    name: rankingName,
    period: rankingPeriods.includes(rankingFilters.period)
      ? rankingFilters.period
      : (rankingPeriods[0] ?? "all"),
  };

  function updateRankingFilters(next: RankingFilters) {
    setRankingFilters(
      next.period === rankingEffective.period
        ? next
        : { ...next, preset: defaultPreset(next.period), from: "", to: "" },
    );
    setRankingPage(1);
  }

  function openTotalsFor(name: string, userId?: string) {
    const def = defFor(name);
    const period = userId && userPeriods(def).includes("day") ? "day" : "";
    setTotalsFilters(
      reconcile(
        { ...EMPTY_FILTERS, name, scope: userId ? "user" : "all", userId: userId ?? "", period },
        totalsEffective,
        def,
        "totals",
      ),
    );
    setTab("totals");
  }

  function selectFromSidebar(def: CounterDefinition) {
    if (userPeriods(def).length > 0) {
      updateRankingFilters({ ...rankingEffective, name: def.name });
      setTab("users");
    } else if (tab === "rows") {
      updateRowFilters({ ...rowFilters, name: def.name });
    } else if (tab === "totals") {
      updateTotalsFilters({ ...totalsEffective, name: def.name });
    }
  }

  // Filters survive the switch, but a loaded user belongs to one universe.
  function changeUniverse(value: string) {
    if (!value) return;
    const next = value as CounterUniverse;
    setUniverse(next);
    saveUniverse(next);
    setRowFilters((f) => ({ ...f, userId: "" }));
    setTotalsFilters((f) => ({ ...f, userId: "" }));
    setPage(1);
    setRankingPage(1);
  }

  const activeName =
    tab === "rows" ? rowFilters.name : tab === "totals" ? totalsEffective.name : rankingEffective.name;
  const topPresets = TOP_PRESETS.filter((p) => defFor(p.name));

  return (
    <>
      <SiteHeader
        title="Counters"
        status={
          <Badge variant={universe === "prod" ? "default" : "secondary"} className="shrink-0 font-mono">
            {universe}
          </Badge>
        }
      />
      <div className="flex flex-1 flex-col overflow-y-auto lg:flex-row lg:overflow-hidden">
        {/* Main content */}
        <div className="flex min-w-0 flex-col gap-4 flex-1 p-4 lg:overflow-y-auto lg:p-6">
          <ToggleGroup
            type="single"
            variant="outline"
            value={universe}
            onValueChange={changeUniverse}
            aria-label="Universe"
          >
            {UNIVERSES.map((u) => (
              <ToggleGroupItem key={u} value={u} className="px-4 font-mono">
                {u}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>

          <DirectionsQuotaCard universe={universe} />

          <Tabs value={tab} onValueChange={(v) => setTab(v as Tab)} className="w-full">
            <TabsList>
              <TabsTrigger value="rows">Rows</TabsTrigger>
              <TabsTrigger value="totals">Totals</TabsTrigger>
              <TabsTrigger value="users">Users</TabsTrigger>
            </TabsList>

            <TabsContent value="rows" className="mt-4 flex flex-col gap-4">
              <CounterFilters
                key={universe}
                value={rowFilters}
                onChange={updateRowFilters}
                definitions={defs}
                mode="rows"
              />
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                <Select
                  value={rowSort ?? ANY}
                  onValueChange={(v) => updateRowSort(v === ANY ? undefined : (v as CounterRowSort))}
                >
                  <SelectTrigger className="w-full sm:w-44" aria-label="Sort">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ROW_SORTS.map((s) => (
                      <SelectItem key={s.value} value={s.value}>
                        {s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {topPresets.map((p) => (
                  <Button
                    key={p.name}
                    variant="link"
                    className="h-9 px-0"
                    onClick={() => applyTopPreset(p.name)}
                  >
                    {p.label}
                  </Button>
                ))}
              </div>
              <RowsPanel
                filters={rowFilters}
                sort={rowSort}
                universe={universe}
                emptyHint={universeHint(rowFilters.name)}
                page={page}
                onPageChange={setPage}
              />
            </TabsContent>

            <TabsContent value="totals" className="mt-4 flex flex-col gap-4">
              <CounterFilters
                key={universe}
                value={totalsEffective}
                onChange={updateTotalsFilters}
                definitions={defs}
                mode="totals"
              />
              <TotalsPanel
                filters={totalsEffective}
                def={totalsDef}
                universe={universe}
                emptyHint={universeHint(totalsEffective.name)}
              />
            </TabsContent>

            <TabsContent value="users" className="mt-4 flex flex-col gap-4">
              <UserRankingPanel
                filters={rankingEffective}
                onChange={updateRankingFilters}
                universe={universe}
                emptyHint={universeHint(rankingEffective.name)}
                definitions={defs}
                page={rankingPage}
                onPageChange={setRankingPage}
                onOpenUser={(userId) => openTotalsFor(rankingEffective.name, userId)}
                onOpenTotals={() => openTotalsFor(rankingEffective.name)}
              />
            </TabsContent>
          </Tabs>
        </div>

        {/* Right sidebar */}
        <aside className="flex w-full shrink-0 flex-col border-t lg:sticky lg:top-0 lg:h-[calc(100vh-var(--header-height,3.5rem))] lg:w-lg lg:border-t-0 lg:border-l">
          <div className="px-4 py-3 flex items-center justify-between gap-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Counters
            </p>
            {definitions && (
              <span className="text-xs text-muted-foreground">{definitions.length}</span>
            )}
          </div>
          <Separator />
          <div className="max-h-80 flex-1 overflow-y-auto px-2 py-2 lg:max-h-none">
            {definitionsLoading && (
              <div className="flex flex-col gap-2 px-2 pt-1">
                {Array.from({ length: 6 }).map((_, i) => (
                  <Skeleton key={i} className="h-8 w-full" />
                ))}
              </div>
            )}
            {!definitionsLoading && defs.length === 0 && (
              <p className="px-2 text-xs text-muted-foreground">No counters in {universe}.</p>
            )}
            {defs.map((def) => (
              <button
                key={def.name}
                type="button"
                onClick={() => selectFromSidebar(def)}
                // The Users tab can only rank counters that have a per-user series.
                disabled={tab === "users" && userPeriods(def).length === 0}
                className={cn(
                  "flex min-h-9 w-full flex-col gap-0.5 rounded-md px-2 py-1.5 text-left hover:bg-accent disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent",
                  activeName === def.name && "bg-accent",
                )}
              >
                <span className="flex w-full items-center justify-between gap-2">
                  <span className="min-w-0 break-all font-mono text-xs">{def.name}</span>
                  <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                    {def.doc_count.toLocaleString()}
                  </span>
                </span>
                {def.series.map((s) => (
                  <span
                    key={`${s.scope}-${s.period}`}
                    className="flex w-full items-center justify-between gap-2 text-xs text-muted-foreground"
                  >
                    <span>
                      {PERIOD_LABELS[s.period]} · {s.scope === "user" ? "per user" : "global"}
                    </span>
                    <span className="shrink-0 tabular-nums">{s.doc_count.toLocaleString()}</span>
                  </span>
                ))}
                <span className="flex w-full flex-wrap items-center justify-between gap-x-2 text-xs text-muted-foreground">
                  <span>
                    Updated {formatDistanceToNow(new Date(def.last_updated_at), { addSuffix: true })}
                  </span>
                  {universesOf(def.name).length > 0 && (
                    <span className="font-mono">{universesOf(def.name).join(" · ")}</span>
                  )}
                </span>
              </button>
            ))}
          </div>
        </aside>
      </div>
    </>
  );
}

// ── Filters ──────────────────────────────────────────────────────────────────

function CounterFilters({
  value,
  onChange,
  definitions,
  mode,
}: {
  value: Filters;
  onChange: (next: Filters) => void;
  definitions: CounterDefinition[];
  // Totals need a counter and a period; the rows list can leave both open.
  mode: FilterMode;
}) {
  const [userDraft, setUserDraft] = useState(value.userId);
  const def = definitions.find((d) => d.name === value.name);
  const bounds = bucketBounds(def, value.scope, value.period);
  // "Any period" only makes sense across counters; one counter's rows should
  // be one comparable kind of value.
  const allowAnyPeriod = mode === "rows" && !value.name;

  const { data: user } = useQuery({
    queryKey: queryKeys.user(value.userId),
    queryFn: () => usersProvider.getById(value.userId),
    enabled: value.scope === "user" && !!value.userId,
    staleTime: 60_000,
  });

  function handleUserSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = userDraft.trim();
    if (trimmed) onChange({ ...value, userId: trimmed });
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap gap-2">
        <Select
          value={value.name || ANY}
          onValueChange={(v) => onChange({ ...value, name: v === ANY ? "" : v })}
        >
          <SelectTrigger className="w-full sm:w-64" aria-label="Counter">
            <SelectValue placeholder="Counter" />
          </SelectTrigger>
          <SelectContent>
            {mode === "rows" && <SelectItem value={ANY}>All counters</SelectItem>}
            {definitions.map((d) => (
              <SelectItem key={d.name} value={d.name}>
                <span className="font-mono text-xs">{d.name}</span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={value.scope} onValueChange={(v) => onChange({ ...value, scope: v as Scope })}>
          <SelectTrigger className="w-full sm:w-44" aria-label="Scope">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {scopesFor(def, mode).map((s) => (
              <SelectItem key={s} value={s}>
                {SCOPE_LABELS[s]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={value.period || ANY}
          onValueChange={(v) => onChange({ ...value, period: v === ANY ? "" : (v as CounterPeriod) })}
        >
          <SelectTrigger className="w-full sm:w-40" aria-label="Period">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {allowAnyPeriod && <SelectItem value={ANY}>Any period</SelectItem>}
            {scopePeriods(def, value.scope).map((p) => (
              <SelectItem key={p} value={p}>
                {PERIOD_LABELS[p]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {(value.period === "month" || value.period === "day") && (
          <div className="flex w-full items-center gap-2 sm:w-auto">
            <BucketPicker
              period={value.period}
              value={value.from}
              onChange={(from) => onChange({ ...value, from })}
              min={bounds.min}
              max={value.to || bounds.max}
              placeholder="From"
              ariaLabel="From bucket"
              className="min-w-0 flex-1 sm:w-40 sm:flex-none"
            />
            <span className="text-xs text-muted-foreground">to</span>
            <BucketPicker
              period={value.period}
              value={value.to}
              onChange={(to) => onChange({ ...value, to })}
              min={value.from || bounds.min}
              max={bounds.max}
              placeholder="To"
              ariaLabel="To bucket"
              className="min-w-0 flex-1 sm:w-40 sm:flex-none"
            />
          </div>
        )}
      </div>

      {value.scope === "user" && (
        <form onSubmit={handleUserSubmit} className="flex w-full items-center gap-2 sm:max-w-lg">
          <div className="relative min-w-0 flex-1">
            <IconSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground pointer-events-none" />
            <Input
              placeholder="User ID…"
              value={userDraft}
              onChange={(e) => setUserDraft(e.target.value)}
              className="pl-8 font-mono text-sm"
            />
          </div>
          <Button type="submit" variant="outline" className="h-9">
            Load
          </Button>
        </form>
      )}

      {value.scope === "user" && value.userId && (
        <div className="flex min-w-0 flex-wrap items-baseline gap-2">
          <span className="text-sm font-medium">
            {user?.username ?? <Skeleton className="inline-block h-4 w-24 align-middle" />}
          </span>
          <span className="truncate font-mono text-xs text-muted-foreground">{value.userId}</span>
        </div>
      )}

      {def && COUNTER_LABELS[def.name] && (
        <p className="text-xs text-muted-foreground">{COUNTER_LABELS[def.name]}</p>
      )}
    </div>
  );
}

// ── Rows ─────────────────────────────────────────────────────────────────────

function RowsPanel({
  filters,
  sort,
  universe,
  emptyHint,
  page,
  onPageChange,
}: {
  filters: Filters;
  sort?: CounterRowSort;
  universe: CounterUniverse;
  emptyHint: string;
  page: number;
  onPageChange: (page: number) => void;
}) {
  const params = {
    universe,
    sort,
    name: filters.name || undefined,
    period: filters.period || undefined,
    from: filters.from || undefined,
    to: filters.to || undefined,
    page,
  };
  const forUser = filters.scope === "user";

  const { data, isLoading, isError, error, isFetching } = useQuery({
    queryKey: forUser
      ? queryKeys.userCounters(filters.userId, params)
      : queryKeys.counters({ ...params, global: filters.scope === "global" }),
    queryFn: () =>
      forUser
        ? countersProvider.listForUser(filters.userId, params)
        : countersProvider.list({ ...params, global: filters.scope === "global" }),
    enabled: !forUser || !!filters.userId,
    // Paging swaps the query key; keep the old rows instead of flashing the skeleton.
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });

  if (forUser && !filters.userId) {
    return <p className="text-sm text-muted-foreground">Enter a user ID above to load their counters.</p>;
  }
  if (isLoading) return <SkeletonRows />;
  if (isError) return <ErrorMsg error={error} />;

  const rows = data?.data ?? [];
  const hasNext = data?.has_next ?? false;

  return (
    <div className="flex flex-col gap-3">
      {rows.length > 0 ? (
        <div className="rounded-lg border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Counter</TableHead>
                <TableHead>Scope</TableHead>
                <TableHead>Period</TableHead>
                <TableHead>Bucket</TableHead>
                <TableHead className="text-right">Value</TableHead>
                <TableHead>Updated</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((c) => (
                <TableRow key={`${c.counter_name}-${c.user_id ?? "global"}-${c.period}-${c.bucket}`}>
                  <TableCell className="font-mono text-sm">{c.counter_name}</TableCell>
                  <TableCell>
                    {c.user_id ? (
                      <CopyId id={c.user_id} />
                    ) : (
                      <span className="text-xs text-muted-foreground">global</span>
                    )}
                  </TableCell>
                  <TableCell className="text-sm">{PERIOD_LABELS[c.period]}</TableCell>
                  <TableCell className="font-mono text-sm text-muted-foreground">
                    {c.bucket || <span className="text-muted-foreground/50">—</span>}
                  </TableCell>
                  <TableCell
                    className="whitespace-nowrap text-right tabular-nums font-mono"
                    title={rawValueTitle(c.counter_name, c.value)}
                  >
                    {formatCounterValue(c.counter_name, c.value)}
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                    {formatDateTime(c.updated_at)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">
          {page > 1
            ? "No more counters."
            : filters.name
              ? `No rows match these filters. ${emptyHint}`
              : "No counters match these filters."}
        </p>
      )}

      <Pager page={data?.page ?? page} hasNext={hasNext} isFetching={isFetching} onPageChange={onPageChange} />
    </div>
  );
}

// No total count is returned, so this is Previous / Next, not pages.
function Pager({
  page,
  hasNext,
  isFetching,
  onPageChange,
}: {
  page: number;
  hasNext: boolean;
  isFetching: boolean;
  onPageChange: (page: number) => void;
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <span className="text-xs text-muted-foreground">
        Page {page}
        {isFetching && " · loading…"}
      </span>
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          className="h-9 gap-1 sm:h-8"
          disabled={page <= 1 || isFetching}
          onClick={() => onPageChange(Math.max(1, page - 1))}
        >
          <IconChevronLeft className="size-4" />
          Previous
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="h-9 gap-1 sm:h-8"
          disabled={!hasNext || isFetching}
          onClick={() => onPageChange(page + 1)}
        >
          Next
          <IconChevronRight className="size-4" />
        </Button>
      </div>
    </div>
  );
}

// ── Users ────────────────────────────────────────────────────────────────────

function UserRankingPanel({
  filters,
  onChange,
  universe,
  emptyHint,
  definitions,
  page,
  onPageChange,
  onOpenUser,
  onOpenTotals,
}: {
  filters: RankingFilters;
  onChange: (next: RankingFilters) => void;
  universe: CounterUniverse;
  emptyHint: string;
  definitions: CounterDefinition[];
  page: number;
  onPageChange: (page: number) => void;
  onOpenUser: (userId: string) => void;
  onOpenTotals: () => void;
}) {
  const def = definitions.find((d) => d.name === filters.name);
  const periods = userPeriods(def);
  const range = resolveRange(filters);
  const enabled = !!filters.name && periods.length > 0 && !range.error;
  const bounds = bucketBounds(def, "user", filters.period);

  const rankingParams = {
    universe,
    name: filters.name,
    period: filters.period,
    from: range.from,
    to: range.to,
    sort: filters.sort,
    page,
  };
  const { data, isLoading, isError, error, isFetching } = useQuery({
    queryKey: queryKeys.counterUserRanking(rankingParams),
    queryFn: () => countersProvider.getUserRanking(rankingParams),
    enabled,
    // Paging swaps the query key; keep the old rows instead of flashing the skeleton.
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });

  // No global flag: the header total is every user's value over the range.
  const totalsParams = {
    universe,
    name: filters.name,
    period: filters.period,
    from: range.from,
    to: range.to,
  };
  const { data: totals } = useQuery({
    queryKey: queryKeys.counterTotals(totalsParams),
    queryFn: () => countersProvider.getTotals(totalsParams),
    enabled,
    staleTime: 30_000,
  });
  const headerTotal = totals?.reduce((acc, t) => acc + t.total, 0);

  const rows = data?.data ?? [];
  const hasNext = data?.has_next ?? false;
  // All time has one row per user, so the rows summed into the all-time total
  // are the user count. A range has no such count; show how many users the
  // pages reached.
  const allTimeUsers = filters.period === "all" ? totals?.[0]?.doc_count : undefined;
  const loadedUsers = (page - 1) * 50 + rows.length;
  const userCount =
    allTimeUsers !== undefined
      ? allTimeUsers.toLocaleString()
      : data
        ? `${loadedUsers.toLocaleString()}${hasNext ? "+" : ""}`
        : undefined;
  const showShare = headerTotal !== undefined && headerTotal > 0;
  const showActiveDays = filters.period === "day";
  const presets = filters.period === "all" ? [] : RANGE_PRESETS[filters.period];

  if (!definitions.length) return null;
  if (!filters.name) {
    return <p className="text-sm text-muted-foreground">No counter has per-user data yet.</p>;
  }

  return (
    <div className="flex flex-col gap-4">
      <p className="min-w-0 text-sm">
        <span className="font-medium">{counterLabel(filters.name)}</span>
        {userCount && <span className="text-muted-foreground"> · {userCount} users</span>}
        {headerTotal !== undefined && (
          <span className="text-muted-foreground" title={rawValueTitle(filters.name, headerTotal)}>
            {" "}· {formatCounterValue(filters.name, headerTotal, true)} total
          </span>
        )}
      </p>

      <div className="flex flex-wrap items-center gap-2">
        <Select value={filters.name} onValueChange={(name) => onChange({ ...filters, name })}>
          <SelectTrigger className="w-full sm:w-64" aria-label="Counter">
            <SelectValue placeholder="Counter" />
          </SelectTrigger>
          <SelectContent>
            {definitions.map((d) => (
              <SelectItem key={d.name} value={d.name} disabled={userPeriods(d).length === 0}>
                <span className="font-mono text-xs">{d.name}</span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {periods.length > 1 && (
          <ToggleGroup
            type="single"
            variant="outline"
            value={filters.period}
            onValueChange={(v) => v && onChange({ ...filters, period: v as CounterPeriod })}
            aria-label="Period"
          >
            {periods.map((p) => (
              <ToggleGroupItem key={p} value={p} className="px-3">
                {PERIOD_LABELS[p]}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        )}

        <Select value={filters.sort} onValueChange={(v) => onChange({ ...filters, sort: v as CounterUserSort })}>
          <SelectTrigger className="w-full sm:w-40" aria-label="Sort">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="value_desc">Highest first</SelectItem>
            <SelectItem value="value_asc">Lowest first</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {presets.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <ToggleGroup
            type="single"
            variant="outline"
            value={filters.preset}
            onValueChange={(v) => v && onChange({ ...filters, preset: v as RangePreset })}
            aria-label="Range"
            className="flex-wrap"
          >
            {presets.map((p) => (
              <ToggleGroupItem key={p.value} value={p.value} className="px-3">
                {p.label}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>

          {filters.preset === "custom" && (filters.period === "month" || filters.period === "day") && (
            <div className="flex w-full items-center gap-2 sm:w-auto">
              <BucketPicker
                period={filters.period}
                value={filters.from}
                onChange={(from) => onChange({ ...filters, from })}
                min={bounds.min}
                max={filters.to || bounds.max}
                placeholder="From"
                ariaLabel="From"
                className="min-w-0 flex-1 sm:w-40 sm:flex-none"
              />
              <span className="text-xs text-muted-foreground">to</span>
              <BucketPicker
                period={filters.period}
                value={filters.to}
                onChange={(to) => onChange({ ...filters, to })}
                min={filters.from || bounds.min}
                max={bounds.max}
                placeholder="To"
                ariaLabel="To"
                className="min-w-0 flex-1 sm:w-40 sm:flex-none"
              />
            </div>
          )}
        </div>
      )}

      {periods.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          This counter has no per-user data.{" "}
          <Button variant="link" className="h-auto p-0" onClick={onOpenTotals}>
            Open it in Totals
          </Button>
        </p>
      ) : range.error ? (
        <p className="text-sm text-destructive">{range.error}</p>
      ) : isLoading ? (
        <SkeletonRows />
      ) : isError ? (
        <ErrorMsg error={error} />
      ) : (
        <div className="flex flex-col gap-3">
          {rows.length > 0 ? (
            <div className="rounded-lg border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    {/* Rank and User stay pinned while the rest scrolls sideways on phones. */}
                    <TableHead className="sticky left-0 z-10 w-12 bg-background">#</TableHead>
                    <TableHead className="sticky left-12 z-10 bg-background">User</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    {showShare && <TableHead className="text-right">Share</TableHead>}
                    {showActiveDays && <TableHead className="text-right">Active days</TableHead>}
                    <TableHead>Updated</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((r) => (
                    <TableRow
                      key={r.user_id}
                      tabIndex={0}
                      onClick={() => onOpenUser(r.user_id)}
                      onKeyDown={(e) => e.key === "Enter" && onOpenUser(r.user_id)}
                      className="cursor-pointer"
                    >
                      <TableCell className="sticky left-0 z-10 w-12 bg-background tabular-nums text-muted-foreground">
                        {r.rank}
                      </TableCell>
                      <TableCell className="sticky left-12 z-10 bg-background">
                        <div className="flex min-w-0 max-w-36 flex-col gap-0.5 overflow-hidden sm:max-w-none">
                          {r.username ? (
                            <span className="truncate text-sm font-medium">{r.username}</span>
                          ) : (
                            <span className="text-sm text-muted-foreground">(deleted user)</span>
                          )}
                          <CopyId id={r.user_id} />
                        </div>
                      </TableCell>
                      <TableCell
                        className="whitespace-nowrap text-right tabular-nums font-mono"
                        title={rawValueTitle(filters.name, r.total)}
                      >
                        {formatCounterValue(filters.name, r.total)}
                      </TableCell>
                      {showShare && (
                        <TableCell className="text-right tabular-nums text-muted-foreground">
                          {((r.total / headerTotal) * 100).toFixed(1)}%
                        </TableCell>
                      )}
                      {showActiveDays && (
                        <TableCell className="text-right tabular-nums text-muted-foreground">
                          {r.rows.toLocaleString()}
                        </TableCell>
                      )}
                      <TableCell
                        className="whitespace-nowrap text-xs text-muted-foreground"
                        title={formatDateTime(r.last_updated_at)}
                      >
                        {formatDistanceToNow(new Date(r.last_updated_at), { addSuffix: true })}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              {page > 1 ? "No more users." : `No user activity for this period. ${emptyHint}`}
            </p>
          )}

          <Pager page={data?.page ?? page} hasNext={hasNext} isFetching={isFetching} onPageChange={onPageChange} />
        </div>
      )}
    </div>
  );
}

// ── Totals ───────────────────────────────────────────────────────────────────

const totalsChartConfig = {
  total: {
    label: "Total",
    color: "var(--primary)",
  },
  doc_count: {
    label: "Users",
    color: "var(--chart-2)",
  },
} satisfies ChartConfig;

function TotalsPanel({
  filters,
  def,
  universe,
  emptyHint,
}: {
  filters: Filters;
  def?: CounterDefinition;
  universe: CounterUniverse;
  emptyHint: string;
}) {
  const period = filters.period || "month";
  const params = {
    universe,
    name: filters.name,
    period,
    global: filters.scope === "global" || undefined,
    user_id: filters.scope === "user" ? filters.userId : undefined,
    from: filters.from || undefined,
    to: filters.to || undefined,
  };

  const { data, isLoading, isError, error } = useQuery({
    queryKey: queryKeys.counterTotals(params),
    queryFn: () => countersProvider.getTotals(params),
    enabled: !!filters.name && (filters.scope !== "user" || !!filters.userId),
    staleTime: 30_000,
  });

  if (!filters.name) return <p className="text-sm text-muted-foreground">Pick a counter to chart.</p>;
  if (filters.scope === "user" && !filters.userId) {
    return <p className="text-sm text-muted-foreground">Enter a user ID above to load their series.</p>;
  }
  if (isLoading) return <Skeleton className="h-[250px] w-full rounded-lg" />;
  if (isError) return <ErrorMsg error={error} />;

  const totals = data ?? [];
  if (totals.length === 0) {
    return <p className="text-sm text-muted-foreground">No data for these filters. {emptyHint}</p>;
  }

  const sum = totals.reduce((acc, t) => acc + t.total, 0);
  // A global series sums one row per bucket, and one user is one row, so the
  // count only means something across every user: daily active users, for
  // daily distance.
  const showUsers =
    filters.scope === "all" && !!def?.series.some((s) => s.scope === "user" && s.period === period);

  return (
    <Card className="@container/card">
      <CardHeader>
        <CardTitle className="break-all font-mono text-base">{filters.name}</CardTitle>
        <CardDescription>
          {period === "all"
            ? "All-time total"
            : `${formatCounterValue(filters.name, sum, true)} across ${totals.length} ${period === "month" ? "months" : "days"}`}
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4 px-2 sm:px-6">
        {period === "all" ? (
          <p
            className="px-4 text-3xl font-semibold tabular-nums sm:px-0"
            title={rawValueTitle(filters.name, sum)}
          >
            {formatCounterValue(filters.name, sum, true)}
          </p>
        ) : (
          <ChartContainer config={totalsChartConfig} className="aspect-auto h-[250px] w-full">
            <ComposedChart data={totals}>
              <CartesianGrid vertical={false} />
              <XAxis
                dataKey="bucket"
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                minTickGap={16}
                tickFormatter={(value: string) => formatBucket(value, period)}
              />
              <YAxis
                yAxisId="total"
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                allowDecimals={false}
                width={48}
                tickFormatter={(value: number) => formatAxisValue(filters.name, value)}
              />
              {showUsers && (
                <YAxis
                  yAxisId="users"
                  orientation="right"
                  tickLine={false}
                  axisLine={false}
                  tickMargin={8}
                  allowDecimals={false}
                  width={40}
                />
              )}
              <ChartTooltip
                cursor={false}
                content={
                  <ChartTooltipContent
                    labelFormatter={(value) => formatBucket(String(value), period)}
                    indicator="dot"
                    formatter={(value, name) => (
                      <div className="flex w-full items-center justify-between gap-4">
                        <span className="text-muted-foreground">
                          {totalsChartConfig[name as keyof typeof totalsChartConfig]?.label ?? name}
                        </span>
                        <span className="font-mono tabular-nums">
                          {name === "total"
                            ? formatCounterValue(filters.name, Number(value), true)
                            : Number(value).toLocaleString()}
                        </span>
                      </div>
                    )}
                  />
                }
              />
              <Bar yAxisId="total" dataKey="total" fill="var(--color-total)" radius={4} />
              {showUsers && (
                <Line
                  yAxisId="users"
                  dataKey="doc_count"
                  type="monotone"
                  stroke="var(--color-doc_count)"
                  strokeWidth={1.5}
                  dot={false}
                />
              )}
            </ComposedChart>
          </ChartContainer>
        )}
        <TotalsTable totals={totals} period={period} name={filters.name} showUsers={showUsers} />
      </CardContent>
    </Card>
  );
}

function TotalsTable({
  totals,
  period,
  name,
  showUsers,
}: {
  totals: CounterTotal[];
  period: CounterPeriod;
  name: string;
  showUsers: boolean;
}) {
  return (
    <div className="max-h-72 overflow-y-auto rounded-lg border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Bucket</TableHead>
            <TableHead className="text-right">Total</TableHead>
            {showUsers && <TableHead className="text-right">Users</TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {[...totals].reverse().map((t) => (
            <TableRow key={t.bucket || "all"}>
              <TableCell className="text-sm">{formatBucket(t.bucket, period)}</TableCell>
              <TableCell className="text-right tabular-nums font-mono" title={rawValueTitle(name, t.total)}>
                {formatCounterValue(name, t.total)}
              </TableCell>
              {showUsers && (
                <TableCell className="text-right tabular-nums text-muted-foreground">
                  {t.doc_count.toLocaleString()}
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

// ── Directions quota ─────────────────────────────────────────────────────────

function DirectionsQuotaCard({ universe }: { universe: CounterUniverse }) {
  // Buckets are UTC, so the current month has to be computed in UTC too.
  const month = utcMonth();
  const params = {
    universe,
    name: "directions_google_request",
    period: "month",
    global: true,
    from: month,
    to: month,
  } as const;

  const { data, isLoading, isError } = useQuery({
    queryKey: queryKeys.counterTotals(params),
    queryFn: () => countersProvider.getTotals(params),
    staleTime: 30_000,
    refetchInterval: 60_000,
  });

  // The backend reserves a slot before every Directions call, including the
  // ones over the limit that then go to Mapbox, so the value keeps climbing
  // past the quota.
  const used = data?.reduce((acc, t) => acc + t.total, 0) ?? 0;
  const toMapbox = Math.max(0, used - DIRECTIONS_QUOTA);
  const shown = Math.min(used, DIRECTIONS_QUOTA);
  const percent = (shown / DIRECTIONS_QUOTA) * 100;
  const barColor = used >= 9_000 ? "bg-destructive" : used >= 7_500 ? "bg-amber-500" : "bg-primary";

  const now = Date.now();
  const monthStart = Date.parse(`${month}-01T00:00:00Z`);
  const nextMonthStart = Date.parse(`${utcMonth(1)}-01T00:00:00Z`);
  const elapsed = (now - monthStart) / (nextMonthStart - monthStart);
  const projected = elapsed > 0 ? Math.round(used / elapsed / 100) * 100 : 0;
  const resetLabel = new Date(nextMonthStart).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });

  return (
    <Card className="gap-3 py-4">
      <CardHeader className="px-4">
        <CardDescription>Google Directions quota · {formatBucket(month, "month")} (UTC)</CardDescription>
        <CardTitle className="text-2xl tabular-nums">
          {isLoading ? (
            <Skeleton className="h-7 w-40" />
          ) : isError ? (
            <span className="text-sm font-normal text-destructive">Couldn't load quota usage.</span>
          ) : (
            <>
              {shown.toLocaleString()}
              <span className="text-base font-normal text-muted-foreground">
                {" "}/ {DIRECTIONS_QUOTA.toLocaleString()}
              </span>
            </>
          )}
        </CardTitle>
      </CardHeader>
      {!isLoading && !isError && (
        <CardContent className="flex flex-col gap-2 px-4">
          <div className="flex items-center gap-3">
            <div className="h-2 min-w-0 flex-1 overflow-hidden rounded-full bg-muted">
              <div className={cn("h-full rounded-full transition-all", barColor)} style={{ width: `${percent}%` }} />
            </div>
            <span className="text-xs tabular-nums text-muted-foreground">{percent.toFixed(1)}%</span>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 text-xs text-muted-foreground">
            <span>Resets {resetLabel}, 00:00 UTC</span>
            {toMapbox > 0 ? (
              <span className="font-medium text-destructive">{toMapbox.toLocaleString()} routed to Mapbox</span>
            ) : (
              used > 0 && (
                <span className={cn(projected > DIRECTIONS_QUOTA && "font-medium text-amber-600 dark:text-amber-500")}>
                  On pace for ~{projected.toLocaleString()} this month
                </span>
              )
            )}
          </div>
        </CardContent>
      )}
    </Card>
  );
}

// ── Small local helpers ──────────────────────────────────────────────────────

function SkeletonRows() {
  return (
    <div className="flex flex-col gap-2">
      {Array.from({ length: 5 }).map((_, i) => (
        <Skeleton key={i} className="h-10 w-full" />
      ))}
    </div>
  );
}

function ErrorMsg({ error }: { error: unknown }) {
  return <p className="text-sm text-destructive">{countersErrorMessage(error)}</p>;
}
